import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { ClientOnly } from "vike-react/ClientOnly";
import { Button } from "#components/Button.tsx";
import FormError from "#components/errors/FormError.tsx";
import Input from "#components/Input.tsx";
import Label from "#components/Label.tsx";
import Notify from "#components/Notify.tsx";
import Turnstile from "#components/Turnstile.tsx";
import { AlertAccent, ThemeButtonVariant } from "#components/theme.ts";
import { handleStringiverseTRPCError } from "#lib/trpc.ts";
import { useRPC } from "#store/trpc.ts";
import AuthForm from "../AuthForm.tsx";

export default function ForgotPasswordPage() {
	const client = useRPC();
	type ForgotPasswordInput = Parameters<
		(typeof client)["auth"]["forgotPassword"]["mutate"]
	>[0];
	const {
		register,
		setValue,
		handleSubmit,
		setError,
		formState: { isValid, errors, isSubmitting },
	} = useForm<
		ForgotPasswordInput & {
			"cf-turnstile-response": string | null;
		}
	>();

	const [formError, setFormError] = useState<string | undefined>();
	const [isSent, setIsSent] = useState(false);

	const onSubmit = useCallback(
		async (input: ForgotPasswordInput) => {
			if (isSent) return;
			try {
				const response = await client.auth.forgotPassword.mutate(input);
				if (response.success) setIsSent(true);
			} catch (e) {
				handleStringiverseTRPCError(e, setError, setFormError);
			}
		},
		[client, isSent, setError],
	);

	return (
		<AuthForm title="Forgot Agent Password" onSubmit={handleSubmit(onSubmit)}>
			<p>
				Enter your agent email address below to reset your account password.
			</p>

			{isSent && (
				<Notify accent={AlertAccent.Success} className="my-4">
					<p>
						A password reset request has been sent to the agent email address if
						it's found in our system.
					</p>
				</Notify>
			)}
			<FormError error={formError} />

			<Label htmlFor="email">Agent Email Address</Label>
			<Input
				type="email"
				id="email"
				{...register("email", {
					required: "An email address is required",
					minLength: 4,
					maxLength: 80,
				})}
				error={errors.email}
				required
				placeholder="agent@example.com"
				className="w-full"
			/>

			<ClientOnly>
				<Turnstile
					className="mt-4"
					error={errors["cf-turnstile-response"]}
					onTokenChange={(token) =>
						setValue("cf-turnstile-response", token ?? "")
					}
				/>
			</ClientOnly>

			<Button
				type="submit"
				disabled={!isValid || (isSent && !formError)}
				loading={isSubmitting}
				loadingText={"Processing..."}
				className="w-full mt-3"
			>
				{isSent ? "Reset Request Sent!" : "Reset Password"}
			</Button>
			<Button
				href="/auth/login"
				variant={ThemeButtonVariant.Outline}
				className="w-full mt-1"
			>
				Back to Login
			</Button>
		</AuthForm>
	);
}
